
import { supabase } from "@/integrations/supabase/client";

// Debounce function to limit API calls
function debounce(func: Function, wait: number) {
  let timeout: ReturnType<typeof setTimeout> | null = null;
  
  return function executedFunction(...args: any[]) {
    const later = () => {
      timeout = null;
      func(...args);
    };
    
    if (timeout) {
      clearTimeout(timeout);
    }
    timeout = setTimeout(later, wait);
  };
}

// Function to update user activity timestamp (both user_activity and engagement_state)
const updateUserActivity = async () => {
  try {
    const { data: { session } } = await supabase.auth.getSession();
    
    if (session?.user) {
      console.log('Updating user activity timestamp for user:', session.user.id);
      
      // Update the legacy user_activity table
      const { error: activityError } = await supabase
        .from('user_activity')
        .upsert(
          { 
            user_id: session.user.id,
            last_active: new Date().toISOString()
          },
          { onConflict: 'user_id' }
        );
      
      if (activityError) {
        console.error('Error updating user activity:', activityError);
      }
      
      // Also update the new engagement_state table
      try {
        await supabase.rpc('update_user_engagement_state', {
          p_user_id: session.user.id,
          p_action_type: 'activity',
          p_action_value: null
        });
      } catch (engagementError) {
        // Silent fail - engagement state might not exist yet for new users
        console.log('Engagement state update skipped:', engagementError);
      }
      
      console.log('User activity updated successfully');
    } else {
      console.log('No active session, skipping activity update');
    }
  } catch (error) {
    console.error('Error updating user activity:', error);
  }
};

// Create a debounced version of the update function (update at most once every 30 seconds)
// Reduced from 60 seconds to 30 seconds for more frequent updates
const debouncedUpdateActivity = debounce(updateUserActivity, 30000);

// Initialize the activity tracker
export const initActivityTracker = () => {
  // Update on initial load
  updateUserActivity();
  
  // Set up event listeners for user interaction - using passive listeners to not block UI
  const events = ['mousedown', 'keydown', 'touchstart'];
  
  const handleActivity = () => {
    debouncedUpdateActivity();
  };
  
  events.forEach(event => {
    document.addEventListener(event, handleActivity, { passive: true });
  });
  
  // Update every 3 minutes regardless of activity
  const intervalId = setInterval(updateUserActivity, 3 * 60 * 1000);
  
  // Update on visibility change (tab focus)
  const handleVisibility = () => {
    if (document.visibilityState === 'visible') {
      updateUserActivity();
    }
  };
  
  document.addEventListener('visibilitychange', handleVisibility, { passive: true });
  
  // Return cleanup function
  return () => {
    events.forEach(event => {
      document.removeEventListener(event, handleActivity as EventListener);
    });
    document.removeEventListener('visibilitychange', handleVisibility);
    clearInterval(intervalId);
  };
};
